import {
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from '@chakra-ui/accordion'
import { Alert, AlertIcon, AlertDescription } from '@chakra-ui/alert'
import { FormControl, FormLabel } from '@chakra-ui/form-control'
import { Flex, Box, VStack, Link, Text as ChakraText } from '@chakra-ui/layout'
import { FormHelperText } from '@chakra-ui/react'
import { Select } from '@chakra-ui/select'
import { Switch } from '@chakra-ui/switch'
import type { FormikProps } from 'formik'
import React, { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { GrOptimize } from 'react-icons/gr'
import { SettingsToggleBox } from '..'
import { PermissionsEnum } from '../../../../utils'
import type { SettingsResponse } from '../../../interfaces'
import PermissionGuard from '../../PermissionGuard'

const VideoPollingValues = {
  '0|5000': '0 seconds',
  '1|5000': '5 seconds',
  '2|5000': '10 seconds',
  '3|5000': '15 seconds',
}

const codecOptions = {
  'h264, vp9': ['h264', 'vp9'],
  'h264, vp9, av1': ['h264', 'vp9', 'av1'],
}

const VideoSettingsOptimization = ({
  values,
  handleChange,
  setFieldValue,
}: FormikProps<SettingsResponse>): React.ReactElement => {
  const { t } = useTranslation()

  const pollingValue = useMemo(
    () =>
      `${values.videoSettings.optimization.polling.attempts}|${values.videoSettings.optimization.polling.wait}`,
    [values.videoSettings.optimization.polling],
  )

  const isCustomValueSet = useMemo(
    () => !Object.keys(VideoPollingValues).includes(pollingValue),
    [pollingValue],
  )

  return (
    <PermissionGuard permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}>
      {(hasPermission: boolean) => (
        <AccordionItem borderTop="none">
          <AccordionButton px={12} py={8}>
            <Flex
              flex="1"
              textAlign="left"
              lineHeight="1.5em"
              fontWeight="700"
              style={{ gap: '1em' }}
            >
              <GrOptimize fontSize="1.5em" />
              {t('video_optimization')}
            </Flex>
            <Box
              background="appPaleGreyThree"
              borderRadius="50%"
              padding="0.25em"
            >
              <AccordionIcon color="appSlate" display="block" />
            </Box>
          </AccordionButton>

          <AccordionPanel px={12}>
            <VStack spacing="0.75em">
              <SettingsToggleBox>
                <Flex justifyContent="space-between" width="100%">
                  <ChakraText fontWeight="700">
                    {t('video_auto_format')}
                  </ChakraText>
                  <Switch
                    name="videoSettings.optimization.format.enabled"
                    isDisabled={!hasPermission}
                    isChecked={values.videoSettings.optimization.format.enabled}
                    onChange={handleChange}
                  />
                </Flex>
                <ChakraText>
                  {t('video_auto_format_description')}
                  <Link
                    isExternal
                    href="https://docs.imagekit.io/features/video-optimization/automatic-video-format-conversion"
                  >
                    {t('read_more', 'Read More')}
                  </Link>
                </ChakraText>
                <ChakraText>
                  {t('we_recommend')} :&nbsp;
                  <ChakraText as="span" color="appGreenishTeal">
                    ON
                  </ChakraText>
                </ChakraText>
                {values.videoSettings.optimization.format.allowedVideoCodecs.includes(
                  'av1',
                ) ? (
                  <Alert status="info">
                    <AlertIcon
                      height="1em"
                      width="1em"
                      alignItems="flex-start"
                      marginTop="4px"
                    />
                    <AlertDescription fontSize="sm">
                      <Trans t={t} i18nKey="av1_enabled">
                        The AV1 output codec is currently in beta. Learn more
                        about AV1 usage pricing{' '}
                        <Link
                          lineHeight={1.2}
                          fontSize="sm"
                          isExternal
                          href="https://docs.imagekit.io/features/video-transformation#pricing"
                        >
                          here
                        </Link>
                        . Reach out to support@imagekit.io for any questions or
                        concerns.
                      </Trans>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <></>
                )}
                <FormControl>
                  <FormLabel>{t('allowed_video_codecs')}</FormLabel>
                  <Select
                    name="videoSettings.optimization.format.allowedVideoCodecs"
                    maxWidth="250px"
                    value={
                      values.videoSettings.optimization.format
                        .allowedVideoCodecs
                    }
                    onChange={e =>
                      void setFieldValue(
                        'videoSettings.optimization.format.allowedVideoCodecs',
                        e.target.value.split(','),
                      )
                    }
                    disabled={
                      !hasPermission ||
                      !values.videoSettings.optimization.format.enabled
                    }
                  >
                    {Object.entries(codecOptions).map(([value, codecs]) => (
                      <option key={value} value={codecs}>
                        {value}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SettingsToggleBox>

              <SettingsToggleBox>
                <Flex justifyContent="space-between" width="100%">
                  <ChakraText fontWeight="700">
                    {t('video_auto_quality')}
                  </ChakraText>
                  <Switch
                    name="videoSettings.optimization.quality.enabled"
                    isDisabled={!hasPermission}
                    isChecked={
                      values.videoSettings.optimization.quality.enabled
                    }
                    onChange={handleChange}
                  />
                </Flex>
                <ChakraText>
                  {t('video_auto_quality_description')}
                  <Link
                    isExternal
                    href="https://docs.imagekit.io/features/video-optimization/quality-optimization"
                  >
                    {t('read_more', 'Read More')}
                  </Link>
                </ChakraText>
                <ChakraText>
                  {t('we_recommend')} :&nbsp;
                  <ChakraText as="span" color="appGreenishTeal">
                    ON
                  </ChakraText>
                </ChakraText>
                <FormControl>
                  <FormLabel>{t('video_default_quality')}</FormLabel>
                  <Select
                    name="optimization.defaultQuality"
                    maxWidth="250px"
                    value={values.videoSettings.optimization.quality.default}
                    onChange={e =>
                      void setFieldValue(
                        'videoSettings.optimization.quality.default',
                        parseInt(e.target.value, 10),
                      )
                    }
                    disabled={
                      !hasPermission ||
                      !values.videoSettings.optimization.quality.enabled
                    }
                  >
                    {[100, 95, 90, 85, 80, 75, 70, 60, 50, 40, 30, 20, 10].map(
                      x => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ),
                    )}
                  </Select>
                </FormControl>
              </SettingsToggleBox>

              <SettingsToggleBox>
                <Flex justifyContent="space-between" width="100%">
                  <ChakraText fontWeight="700">
                    {t('video_polling_duration')}
                  </ChakraText>
                </Flex>
                <ChakraText whiteSpace="pre-line">
                  {t('video_polling_duration_description')}
                  <Link
                    isExternal
                    href="https://docs.imagekit.io/features/video-transformation#limitations"
                  >
                    {t('read_more', 'Read More')}
                  </Link>
                </ChakraText>
                <ChakraText>
                  {t('we_recommend')} :&nbsp;
                  <ChakraText as="span" color="appGreenishTeal">
                    15 Seconds
                  </ChakraText>
                </ChakraText>
                <FormControl>
                  <Select
                    name="optimization.polling"
                    maxWidth="250px"
                    value={pollingValue}
                    onChange={e => {
                      const [attempts, wait] = e.target.value.split('|')
                      void setFieldValue(
                        'videoSettings.optimization.polling.attempts',
                        parseInt(attempts, 10),
                      )
                      void setFieldValue(
                        'videoSettings.optimization.polling.wait',
                        parseInt(wait, 10),
                      )
                    }}
                    disabled={!hasPermission || isCustomValueSet}
                  >
                    {isCustomValueSet ? (
                      <option>
                        {`${
                          (values.videoSettings.optimization.polling.attempts *
                            values.videoSettings.optimization.polling.wait) /
                          1000
                        } Seconds`}
                      </option>
                    ) : (
                      Object.entries(VideoPollingValues).map(
                        ([value, label]) => (
                          <option value={value}>{label}</option>
                        ),
                      )
                    )}
                  </Select>

                  <FormHelperText fontStyle="italic">
                    {t('video_polling_duration_subtext')}
                  </FormHelperText>
                </FormControl>
                {isCustomValueSet ? (
                  <Alert status="warning">
                    <AlertIcon
                      height="1em"
                      width="1em"
                      alignItems="flex-start"
                      marginTop="4px"
                    />
                    <AlertDescription fontSize="sm">
                      This setting is locked on your account. Reach out to
                      support@imagekit.io for any questions or concerns.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <></>
                )}
              </SettingsToggleBox>
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      )}
    </PermissionGuard>
  )
}

export default VideoSettingsOptimization
